import React, { useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import CollapsibleSection from "../../pages/common-features/CollapsibleSection";
import DetailRow from "../../pages/common-features/DetailRow";
import SummarySectionHeader from "../../components/applicantDetailsTabs/SummarySectionHeader";

interface ReferenceField {
  label: string;
  name: string;
  value: string | number;
}

interface ReferenceDetailsProps {
  referenceDetails: Array<{
    [key: string]: ReferenceField[];
  }>;
  isMobile: boolean;
  pageTypeSummary?:string;
  partnerStartedJourny?:boolean;
}

export const normalizeFieldLabel = (label: string) =>
  label?.toLowerCase().replace(/[^a-z0-9]+/g, "") ?? "";

export const orderFieldsByPreference = (
  fields: ReferenceField[],
  preferredOrder: string[]
) =>
  [...fields].sort((a, b) => {
    const indexA = preferredOrder.findIndex(
      (label) => normalizeFieldLabel(label) === normalizeFieldLabel(a.label)
    );
    const indexB = preferredOrder.findIndex(
      (label) => normalizeFieldLabel(label) === normalizeFieldLabel(b.label)
    );

    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });

// Skeleton loader for reference details grid
export const referenceDetailsSkeleton = () =>
  Array.from({ length: 2 }).map((_, idx) => (
    <Grid item xs={12} key={`reference-skeleton-${idx}`}>
      <Box sx={{ mb: 1 }}>
        <Box
          sx={{
            width: "100%",
            height: 75,
            bgcolor: "#eee",
            borderRadius: 1,
            mb: 1,
          }}
        />
      </Box>
    </Grid>
  ));

const ReferenceDetailsTab: React.FC<ReferenceDetailsProps> = ({
  referenceDetails,
  isMobile,
  pageTypeSummary,
  partnerStartedJourny,
}) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  if (!referenceDetails || referenceDetails.length === 0)
    return (
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          No details available.
        </Typography>
      </Grid>
    );

  // Basic details fields
  const basicLabels = [
    "First Name",
    "Last Name",
    "Email",
    "Phone Number",
    "Reference First Name",
    "Reference Last Name",
    "Reference Email Address",
    "Reference Mobile",
  ];

  // Basic + Address details fields
  const fieldOrder = [
    "Address Line 1",
    "Address Line 2",
    "Address Line 3",
    "Locality",
    "Landmark",
    "Area",
    "Pincode",
    "City",
    "State",
    "Country",
  ];

  const getSortedFields = (fields: ReferenceField[]) => {
    if (!fields) return [];

    const removePrefix = (label: string) => {
      if (!label) return "";
      return label.replace(/^(Basic|Address|Employment)\s+Reference\d*\s*/i, "").trim();
    };
    const cleanedFields = fields.map(field => ({
      ...field,
      label: removePrefix(field.label) 
    }));

    return cleanedFields;
  };

  // sub-header style
  const subHeaderStyle = {
    fontSize: "15px",
    fontWeight: 600,
    color: "#6f6b7d",
    mb: 3,
  };

  return (
    <Grid item xs={12}>
      {referenceDetails?.map((refSection, idx) => {
        const refKey = Object.keys(refSection)[0];
        const rawFields = refSection[refKey];
        const sortedFields = getSortedFields(rawFields);
        const isExpanded = expandedIndex === idx;

        const basicFields = sortedFields.filter((f) => basicLabels.includes(f.label));
        const addressFields = sortedFields.filter((f) => !basicLabels.includes(f.label));
        const orderedAddressFields = orderFieldsByPreference(addressFields, fieldOrder);
        
        return (
          <Box key={refKey}>
            <CollapsibleSection
              title={`Reference ${idx + 1}`}
              isExpanded={pageTypeSummary==="loansummary"?true:isExpanded}
              onToggle={() => setExpandedIndex(isExpanded ? null : idx)}
            >
              {/* basic details */}
              {basicFields.length > 0 && (
                <Box sx={{ mb: 5 }}>
                  {
                    pageTypeSummary!=="loansummary" && 
                    partnerStartedJourny===true &&
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2, mt: 2 }}>
                        <SummarySectionHeader
                          title={"Basic Details"}
                        />
                      </Box>
                    </Grid>
                  }
                  {
                    pageTypeSummary!=="loansummary" && 
                    partnerStartedJourny===false && 
                    <Typography sx={{ ...subHeaderStyle }}>
                      Basic Details
                    </Typography>
                  }
                  {
                    pageTypeSummary==="loansummary" && 
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2, mt: 2 }}>
                        <SummarySectionHeader
                          title={"Basic Details"}
                        />
                      </Box>
                    </Grid>
                  }
                  <Grid container spacing={2} sx={{ ml: isMobile ? 0.2 : -2, width: isMobile ? 'auto' : 'calc(100% + 16px)' }}>
                    {basicFields.map((field) => (
                      <Grid item xs={12} sm={6} md={3} key={field.name}>
                        <DetailRow item={field} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* address details */}
              {addressFields.length > 0 && (
                <Box>
                  {
                    pageTypeSummary!=="loansummary" && 
                    partnerStartedJourny===true &&
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2, mt: 2 }}>
                        <SummarySectionHeader
                          title={"Address Details"}
                        />
                      </Box>
                    </Grid>
                  }
                  {
                    pageTypeSummary!=="loansummary" && 
                    partnerStartedJourny===false && 
                    <Typography sx={{ ...subHeaderStyle }}>
                      Address Details
                    </Typography>
                  }
                  {
                    pageTypeSummary==="loansummary" && 
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2, mt: 2 }}>
                        <SummarySectionHeader
                          title={"Address Details"}
                        />
                      </Box>
                    </Grid>
                  }
                  <Grid container spacing={2} sx={{ ml: isMobile ? 0.2 : -2, width: isMobile ? 'auto' : 'calc(100% + 16px)' }}>
                    {orderedAddressFields?.map((field) => (
                      <Grid item xs={12} sm={6} md={3} key={field.name}>
                        <DetailRow item={field} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </CollapsibleSection>
          </Box>
        );
      })}
    </Grid>
  );
};

export default ReferenceDetailsTab;
